import React from 'react'
import { useSelector } from 'react-redux'
import { AdminApi } from '../admin.api'
import { IStoreInterface } from '../../configs/store.config'
import { Contact, LoggedUser } from '../../shared/shared.models'
import { Column, PageTitle, Row } from '../../shared/shared.styled'
import { useAlert } from 'react-alert'
import {
    Table,
    ColumnProps,
} from '../../shared/components/table/table.component'
import Loader from 'react-spinners/DotLoader'
import { Typography, Colors } from '../../configs/styled.config'
import styled from 'styled-components'
import { useFilter } from '../../shared/hooks/use-filter.hook'
import TranslationUtils from '../../shared/utils/translations.utils'
import { SortOrderEnum, SortUtils } from '../../shared/sort.utils'
import ContactSearch from '../components/contacts-search.component'
import ActionButton from '../../shared/components/action-button.component'
import { useNavigate } from 'react-router-dom'
import { RoutesEnum } from '../../routes.constants'
import { RoleEnum, StatusEnum } from '../../shared/shared.enums'
import { ContactAnalytics } from '../components/contact-analytics.component'
import { ContactFilters } from '../components/contacts-search.component'
import moment from 'moment'
import { Tooltip } from '@mui/material'
import PdfGeneratorButton from '../../shared/components/button2.component'
import { enqueueCompanyContacts } from '../../api/celeryClient'
import useArtifactDownload from '../../hooks/useArtifactDownload'
import { useIsAdmin } from '../../shared/hooks/use-is-admin.hook'
// import { Tooltip } from 'react-tooltip'

export const ContactsPage = () => {
    // Hooks initialization
    const navigate = useNavigate()
    const { filters, updateContactFilter, clearAllFilters } = useFilter()
    const [isFetchingContacts, setIsFetchingContacts] = React.useState(false)

    const alert = useAlert()
    const company = useSelector(
        (state: IStoreInterface) => state.adminReducer.company
    )

    const { startTask: startContactsCSVTask } = useArtifactDownload({
        getFilename: () => `Lista de Contatos - ${company ? company.name : ''}`,
        successMessage: 'O CSV será transferido automaticamente',
        failureMessage: 'Erro ao gerar o CSV. Por favor tente mais tarde',
        defaultContentType: 'text/csv',
    })

    const isAdmin = useIsAdmin()

    // Redux state
    const loggedUser = useSelector(
        (state: IStoreInterface) => state.adminReducer.user as LoggedUser
    )
    // Local state
    const [contacts, setContacts] = React.useState<Contact[]>([])
    const [isLoading, setIsLoading] = React.useState(true)
    const [isInitialLoad, setIsInitialLoad] = React.useState(true)

    const navigateToCreateContact = () => {
        navigate(RoutesEnum.CREATE_CONTACT)
    }

    // Function to fetch contacts with filters
    const fetchContactsWithFilters = React.useCallback(
        async (appliedFilters: any) => {
            setIsFetchingContacts(true)
            try {
                const res = await AdminApi.methods.getContacts(
                    loggedUser.companyUuid,
                    appliedFilters
                )
                setContacts(res.data.contacts)
            } catch (error) {
                alert.error(
                    'Erro ao buscar os contatos. Por favor tente mais tarde'
                )
            } finally {
                setIsFetchingContacts(false)
                setIsLoading(false)
            }
        },
        [alert, loggedUser.companyUuid]
    )

    // Effect to set default filter and fetch initial contacts
    React.useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search)
        if (queryParams.toString() === '') {
            // No URL parameters - set default status filter. Let the filters effect
            // handle fetching so we only perform one request.
            updateContactFilter(ContactFilters.STATUS, StatusEnum.OPEN)
        } else {
            // URL parameters exist - parse them and update the filter state.
            const urlFilters: any = {}
            queryParams.forEach((value, key) => {
                const filterKey = key.toUpperCase() as ContactFilters
                if (Object.values(ContactFilters).includes(filterKey)) {
                    urlFilters[filterKey] = value
                    updateContactFilter(filterKey, value)
                }
            })
        }
        // Mark initial load complete; the filters effect will now fetch once.
        setIsInitialLoad(false)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    // Effect to fetch contacts when filters change
    React.useEffect(() => {
        // Skip the initial load, but handle all subsequent filter changes
        if (!isInitialLoad) {
            fetchContactsWithFilters(filters)
        }
    }, [filters, fetchContactsWithFilters, isInitialLoad])

    const getColumns = (): ColumnProps[] => {
        if (
            loggedUser.role === RoleEnum.ADMIN ||
            loggedUser.role === RoleEnum.MANAGER
        ) {
            return [
                {
                    key: 'date',
                    label: 'Data',
                    ratio: 2 / 10,
                    renderFunction: (contact) => contact.date,
                    onClick: onHeaderClick,
                },
                {
                    key: 'name',
                    ratio: 2 / 10,
                    label: 'Nome',
                    renderFunction: (contact) => contact.name,
                    onClick: onHeaderClick,
                },
                {
                    key: 'status',
                    label: 'Situação',
                    ratio: 1 / 10,
                    renderFunction: (contact) =>
                        TranslationUtils.getContactStatusTranslation(
                            contact.status
                        ),
                    onClick: onHeaderClick,
                },
                {
                    key: 'phone',
                    label: 'Nº Telemóvel',
                    ratio: 1 / 10,
                    renderFunction: (contact) => contact.phone,
                },
                {
                    key: 'seller',
                    label: 'Vendedor',
                    ratio: 2 / 10,
                    renderFunction: (contact) => contact.contactUserName,
                    onClick: onHeaderClick,
                },
                {
                    key: 'iteractions',
                    label: 'Última iteração',
                    ratio: 4 / 10,
                    renderFunction: (contact) =>
                        contact.iteractions?.length > 0 ? (
                            <>
                                <Tooltip
                                    title={
                                        contact.iteractions[
                                            contact.iteractions.length - 1
                                        ].description
                                    }
                                >
                                    <span
                                        style={{
                                            color: '#44566c',
                                        }}
                                    >
                                        {
                                            contact.iteractions[
                                                contact.iteractions.length - 1
                                            ].description
                                        }
                                    </span>
                                </Tooltip>
                            </>
                        ) : (
                            <span style={{ color: '#db8835' }}>
                                Sem iterações
                            </span>
                        ),
                },
                {
                    key: 'iteractions',
                    label: 'Data da iteração',
                    ratio: 1 / 10,
                    renderFunction: (contact) =>
                        contact.iteractions?.length > 0 ? (
                            <>
                                <span
                                    style={{
                                        color: '#44566c',
                                    }}
                                >
                                    {moment(
                                        contact.iteractions[
                                            contact.iteractions.length - 1
                                        ].updatedAt
                                    ).format('DD/MM/YYYY')}
                                </span>
                            </>
                        ) : (
                            <span style={{ color: '#db8835' }}>
                                Sem iterações
                            </span>
                        ),
                },
            ]
        } else {
            return [
                {
                    key: 'date',
                    label: 'Data',
                    ratio: 2 / 10,
                    renderFunction: (contact) => contact.date,
                    onClick: onHeaderClick,
                },
                {
                    key: 'name',
                    ratio: 2 / 10,
                    label: 'Nome',
                    renderFunction: (contact) => contact.name,
                    onClick: onHeaderClick,
                },
                {
                    key: 'status',
                    label: 'Situação',
                    ratio: 1 / 10,
                    renderFunction: (contact) =>
                        TranslationUtils.getContactStatusTranslation(
                            contact.status
                        ),
                    onClick: onHeaderClick,
                },
                {
                    key: 'phone',
                    label: 'Nº Telemóvel',
                    ratio: 1 / 10,
                    renderFunction: (contact) => contact.phone,
                },
                {
                    key: 'iteractions',
                    label: 'Última iteração',
                    ratio: 4 / 10,
                    renderFunction: (contact) =>
                        contact.iteractions?.length > 0 ? (
                            <Tooltip
                                title={
                                    contact.iteractions[
                                        contact.iteractions.length - 1
                                    ].description
                                }
                            >
                                <span
                                    style={{
                                        color: '#44566c',
                                    }}
                                >
                                    {
                                        contact.iteractions[
                                            contact.iteractions.length - 1
                                        ].description
                                    }
                                </span>
                            </Tooltip>
                        ) : (
                            <span style={{ color: '#db8835' }}>
                                Sem iterações
                            </span>
                        ),
                },
                {
                    key: 'iteractions',
                    label: 'Data da iteração',
                    ratio: 1 / 10,
                    renderFunction: (contact) =>
                        contact.iteractions?.length > 0 ? (
                            <>
                                <span
                                    style={{
                                        color: '#44566c',
                                    }}
                                >
                                    {moment(
                                        contact.iteractions[
                                            contact.iteractions.length - 1
                                        ].updatedAt
                                    ).format('DD/MM/YYYY')}
                                </span>
                            </>
                        ) : (
                            <span style={{ color: '#db8835' }}>
                                Sem iterações
                            </span>
                        ),
                },
            ]
        }
    }

    const onHeaderClick = (header: string, order: SortOrderEnum) => {
        const sortedContacts = SortUtils.sortContacts(contacts, header, order)
        setContacts(sortedContacts)
    }

    if (isLoading) {
        return (
            <Column
                style={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    right: 0,
                    left: 0,
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Loader color={Colors['gold-pmauto']} />
                <LoadingText>A carregar os seus contatos...</LoadingText>
            </Column>
        )
    }

    const editContact = (contact: Contact) => {
        navigate(RoutesEnum.CONTACT_PAGE.replace(':contactId', contact.uuid))
    }

    const enqueueCSVContacts = async (
        companyUuid: string,
        companyName: string
    ) => {
        try {
            const res = await enqueueCompanyContacts(companyUuid)
            startContactsCSVTask(res.task_id)
        } catch (err) {
            console.error(err)
            alert.error('Erro ao enfileirar o CSV de contactos')
        }
    }

    return (
        <>
            <Row style={{ justifyContent: 'space-between' }}>
                <PageTitle>Contatos</PageTitle>
                {isAdmin && (
                    <PdfGeneratorButton
                        icon={'/icons/csv.png'}
                        action={() =>
                            enqueueCSVContacts(
                                company ? company.uuid : '',
                                company ? company.name : ''
                            )
                        }
                        title={''}
                    />
                )}
            </Row>
            <ContactSearch
                clearAllFilters={clearAllFilters}
                updateFilter={updateContactFilter}
                filters={filters}
            />
            <ContactAnalytics
                allContacts={contacts}
                visibleContacts={contacts}
            />
            <Table<Contact>
                isLoadingData={isFetchingContacts}
                rowStyles={(value) =>
                    value.status === StatusEnum.SOLD
                        ? { backgroundColor: '#B3E1A5' }
                        : value.status === StatusEnum.OPEN
                          ? { backgroundColor: '#fcdede' }
                          : value.status === StatusEnum.CLOSED
                            ? {}
                            : {}
                }
                columns={getColumns()}
                onRowClick={editContact}
                getUrl={(value) =>
                    RoutesEnum.CONTACT_PAGE.replace(':contactId', value.uuid)
                }
                values={contacts}
                contactsExists={contacts?.length > 0 ? true : false}
            />

            <ActionButton onClick={navigateToCreateContact} />
        </>
    )
}

const LoadingText = styled('span')`
    margin-top: 24px;
    ${Typography['Body_Text_#1_Regular_Center']}
`
